import React from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import FormGroup from "@/components/FormGroup";

export interface DisplayCondition {
  type: "url_contains" | "url_equals" | "url_matches" | "always";
  value: string;
}

export interface DisplayRule {
  id: string;
  operator: "any" | "all";
  conditions: DisplayCondition[];
  showAfterSeconds: number;
  showAfterScrollPercent: number;
}

function newRule(): DisplayRule {
  return {
    id: Math.random().toString(36).slice(2),
    operator: "any",
    conditions: [{ type: "url_contains", value: "" }],
    showAfterSeconds: 0,
    showAfterScrollPercent: 0
  };
}

interface Props {
  rules: DisplayRule[];
  onChange(rules: DisplayRule[]): void;
}

const CONDITION_TYPE_LABELS: Record<DisplayCondition["type"], string> = {
  url_contains: "URL contains",
  url_equals: "URL equals",
  url_matches: "URL matches pattern",
  always: "Always show"
};

export default function DisplayRulesEditor({ rules, onChange }: Props) {
  function addRule() {
    onChange([...rules, newRule()]);
  }

  function removeRule(id: string) {
    onChange(rules.filter((r) => r.id !== id));
  }

  function updateRule(id: string, updates: Partial<DisplayRule>) {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  }

  function addCondition(ruleId: string) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? { ...r, conditions: [...r.conditions, { type: "url_contains", value: "" }] }
          : r
      )
    );
  }

  function removeCondition(ruleId: string, index: number) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? { ...r, conditions: r.conditions.filter((_, i) => i !== index) }
          : r
      )
    );
  }

  function updateCondition(ruleId: string, index: number, updates: Partial<DisplayCondition>) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              conditions: r.conditions.map((c, i) => (i === index ? { ...c, ...updates } : c))
            }
          : r
      )
    );
  }

  if (rules.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
        <div className="text-gray-400 text-sm">No display rules — widget shows on all pages</div>
        <PrimaryBlueButton label="Add Display Rule" onClick={addRule} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {rules.map((rule, ruleIndex) => (
        <div key={rule.id} className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm text-gray-700">
              Rule {ruleIndex + 1}
            </div>
            <button
              onClick={() => removeRule(rule.id)}
              className="text-red-400 hover:text-red-600 text-xs"
              aria-label="Remove rule"
            >
              Remove rule
            </button>
          </div>

          {/* Conditions */}
          <div className="flex flex-col gap-2">
            {rule.conditions.map((condition, condIndex) => (
              <div key={condIndex} className="flex items-center gap-2">
                {condIndex > 0 && (
                  <span className="text-xs text-gray-400 w-6 text-center">OR</span>
                )}
                {condIndex === 0 && <span className="w-6" />}

                <select
                  className="input text-sm flex-shrink-0"
                  value={condition.type}
                  onChange={(e) =>
                    updateCondition(rule.id, condIndex, { type: e.target.value as DisplayCondition["type"] })
                  }
                >
                  {Object.entries(CONDITION_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>

                {condition.type !== "always" && (
                  <input
                    className="input text-sm flex-1"
                    value={condition.value}
                    onChange={(e) => updateCondition(rule.id, condIndex, { value: e.target.value })}
                    placeholder={
                      condition.type === "url_contains" ? "/products"
                      : condition.type === "url_equals" ? "https://example.com/pricing"
                      : "/blog/*"
                    }
                  />
                )}

                {rule.conditions.length > 1 && (
                  <button
                    onClick={() => removeCondition(rule.id, condIndex)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    aria-label="Remove condition"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => addCondition(rule.id)}
            className="text-cornflower-blue-500 text-xs hover:underline text-left"
          >
            + Add another URL condition
          </button>

          {/* Timing */}
          <div className="flex gap-4 pt-2 border-t border-gray-100">
            <FormGroup label="Show after (seconds)" hint="0 = immediate">
              <input
                type="number"
                className="input w-24 text-sm"
                min={0}
                max={300}
                value={rule.showAfterSeconds}
                onChange={(e) =>
                  updateRule(rule.id, { showAfterSeconds: Math.max(0, Number(e.target.value)) })
                }
              />
            </FormGroup>
            <FormGroup label="Show after scroll %" hint="0 = any scroll">
              <input
                type="number"
                className="input w-24 text-sm"
                min={0}
                max={100}
                value={rule.showAfterScrollPercent}
                onChange={(e) =>
                  updateRule(rule.id, { showAfterScrollPercent: Math.max(0, Math.min(100, Number(e.target.value))) })
                }
              />
            </FormGroup>
          </div>
        </div>
      ))}

      <PrimaryBlueButton label="+ Add Another Rule" onClick={addRule} />
    </div>
  );
}
